import { Store } from 'container/app/store';
import { action, observable, runInAction } from 'mobx';
import AppStore from 'container/app/appStore';
import { AddFolderModalStore } from '../../../pages/dashboard/components/dbFolderContainer/folderModals/addFolderModalStore';
import { createDocument, createFolder } from 'api/graphql/mutation';
import { ErrorModel } from 'components/error/errorModel';
import { NAVIGATION } from 'constants/navigation';
import { CreateDocumentModalStore } from '../../../pages/dashboard/createDocumentModalStore';
import { PreferenceModalStore } from 'pages/dashboard/preferenceModalStore';
import { createPreferences } from 'api/graphql/mutation';
import { getPreferences, listWhatsNew } from 'api/graphql/query';
import { WhatsNewEntry } from 'components/whatsNew/whatsNewConfig';
import { recordRumEvent, RumEventType } from 'utils/cloudWatchRUM';

// One "What's New" fetch returns this many announcements; the frontend pages via
// the backend cursor as the user scrolls the modal.
const WHATS_NEW_PAGE_SIZE = 20;

/** Best-effort message from a rejected API call (Error, or an Amplify GraphQL `{ errors }` result). */
const errorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  const first = (error as { errors?: { message?: string }[] })?.errors?.[0]?.message;
  return first ?? 'unknown';
};

export class AppTopNavStore implements Store {
  @observable appStore: AppStore;
  @observable folderModalStore!: AddFolderModalStore;
  @observable documentModalStore!: CreateDocumentModalStore;
  @observable preferenceModalStore!: PreferenceModalStore;
  @observable organizationUrl = '';
  @observable showPreference = false;
  @observable showWhatsNew = false;
  @observable whatsNewEntries: WhatsNewEntry[] = [];
  @observable whatsNewLoading = false;
  // Operator on/off flag from the backend — gates ONLY the auto-open (the sparkle
  // icon and manual open always work). False when there's nothing to announce.
  @observable whatsNewEnabled = false;

  /** Set once the first page loads successfully; prevents re-fetching page 1 every time the panel is reopened. */
  @observable whatsNewLoaded = false;
  // Opaque cursor for the next page of announcements ('' = no more pages).
  @observable whatsNewCursor = '';
  // True while a "load more" (scroll) fetch is in flight — drives the bottom spinner.
  @observable whatsNewLoadingMore = false;
  // Set when a "load more" fetch fails. Suppresses further automatic (scroll-driven)
  // load-more attempts so a failing backend isn't re-queried in a tight loop; cleared
  // by an explicit retry (retryLoadMoreWhatsNew) or a fresh first-page load.
  @observable whatsNewLoadMoreFailed = false;
  // Bumped on each first-page load; lets an in-flight loadMore detect that the list
  // was replaced and discard its now-stale page. Internal (not observed by the view).
  private whatsNewGeneration = 0;
  // In-flight first-page load, shared so concurrent callers (auto-open on mount +
  // a manual sparkle click) await the SAME real fetch instead of a resolved no-op.
  private whatsNewLoadPromise: Promise<void> | null = null;
  constructor(appStore: AppStore) {
    this.appStore = appStore;
  }

  @action.bound
  toggleWhatsNew(open?: boolean): void {
    this.showWhatsNew = open ?? !this.showWhatsNew;
  }

  /**
   * Fetch the first page of announcements (listWhatsNew). On any error or empty
   * result, `whatsNewEntries` is left empty so the modal never opens — there is
   * no static fallback. Concurrent calls (auto-open on mount + a manual sparkle
   * click) are coalesced onto a single in-flight fetch, and every caller awaits
   * that real fetch — so `maybeAutoOpenWhatsNew` never reads stale/empty state.
   */
  @action.bound
  loadWhatsNew(): Promise<void> {
    // Coalesce overlapping first-page loads: return the in-flight promise so the
    // caller awaits the actual fetch (not a resolved no-op), and only one fetch
    // runs at a time (so a stale older fetch can't overwrite the list/cursor).
    if (this.whatsNewLoadPromise) return this.whatsNewLoadPromise;
    this.whatsNewLoadPromise = this.runLoadWhatsNew();
    return this.whatsNewLoadPromise;
  }

  @action.bound
  private async runLoadWhatsNew(): Promise<void> {
    // Bump the generation so any in-flight loadMore discards its now-stale page.
    this.whatsNewGeneration += 1;
    this.whatsNewLoading = true;
    this.whatsNewLoadMoreFailed = false;
    try {
      const { data } = await this.appStore.api.query(listWhatsNew, { pageSize: WHATS_NEW_PAGE_SIZE });
      // Backend returns { enabled, announcements, lastEvalKey }. `enabled` is the
      // operator flag; `announcements` is the first page; `lastEvalKey` is the
      // cursor for the next page ('' when there are none). All fall back safely.
      const response = data?.listWhatsNew ?? {};
      const entries: WhatsNewEntry[] = response.announcements ?? [];
      const enabled: boolean = response.enabled === true;
      runInAction(() => {
        this.whatsNewEntries = entries;
        this.whatsNewEnabled = enabled;
        this.whatsNewCursor = response.lastEvalKey ?? '';
        this.whatsNewLoaded = true;
      });
    } catch (error) {
      // Fail closed (no feed, no auto-open, no badge), but report it so a backend
      // outage is visible in CloudWatch RUM rather than silently leaving the feature dark.
      recordRumEvent(RumEventType.WhatsNewLoadFailed, { phase: 'firstPage', message: errorMessage(error) });
      runInAction(() => {
        this.whatsNewEntries = [];
        this.whatsNewEnabled = false;
        this.whatsNewCursor = '';
      });
    } finally {
      runInAction(() => { this.whatsNewLoading = false; });
      this.whatsNewLoadPromise = null;
    }
  }

  /**
   * Append the next page of announcements (infinite scroll). No-op if a load is
   * already in flight, there is no cursor (no more pages), or the previous attempt
   * failed. On error the loaded list and cursor are kept and `whatsNewLoadMoreFailed`
   * is set, so the panel stops auto-retrying and offers an explicit "Try again".
   */
  @action.bound
  async loadMoreWhatsNew(): Promise<void> {
    if (this.whatsNewLoadingMore || !this.whatsNewCursor || this.whatsNewLoadMoreFailed) return;
    this.whatsNewLoadingMore = true;
    // Capture the generation; if a fresh loadWhatsNew (replace) runs while this
    // fetch is in flight, discard the stale page instead of corrupting the list.
    const generation = this.whatsNewGeneration;
    try {
      const { data } = await this.appStore.api.query(listWhatsNew, {
        pageSize: WHATS_NEW_PAGE_SIZE,
        lastEvalKey: this.whatsNewCursor,
      });
      const response = data?.listWhatsNew ?? {};
      const more: WhatsNewEntry[] = response.announcements ?? [];
      runInAction(() => {
        if (generation !== this.whatsNewGeneration) return; // list was replaced; drop stale page
        this.whatsNewEntries.push(...more);
        this.whatsNewCursor = response.lastEvalKey ?? '';
      });
    } catch (error) {
      // Keep the loaded list and cursor, but stop automatic retries until the user
      // explicitly asks (the sentinel would otherwise re-fire immediately).
      recordRumEvent(RumEventType.WhatsNewLoadFailed, { phase: 'loadMore', message: errorMessage(error) });
      runInAction(() => {
        if (generation === this.whatsNewGeneration) this.whatsNewLoadMoreFailed = true;
      });
    } finally {
      runInAction(() => { this.whatsNewLoadingMore = false; });
    }
  }

  /** Explicit user retry after a failed "load more": clear the failure and fetch once. */
  @action.bound
  retryLoadMoreWhatsNew(): Promise<void> {
    this.whatsNewLoadMoreFailed = false;
    return this.loadMoreWhatsNew();
  }

  /**
   * Auto-open the What's New modal at most ONCE PER DAY, and only when the
   * operator flag is on and there are announcements. A localStorage key holds the
   * last day (YYYY-MM-DD) it auto-opened, so it re-opens the next calendar day.
   * This is the single place that decides *when* the modal auto-opens. The nav
   * icon + manual open are NOT gated by this (they always work).
   *
   * The feed is loaded on EVERY call (not only the first of the day): the nav badge
   * is driven by `whatsNewEnabled`, which only a load populates, so skipping the
   * fetch on later page loads would hide the badge while the flag is still on.
   */
  @action.bound
  async maybeAutoOpenWhatsNew(): Promise<void> {
    await this.loadWhatsNew();
    // Local calendar day as YYYY-MM-DD. Use the LOCAL date (en-CA formats as
    // YYYY-MM-DD), NOT toISOString() which is UTC and would flip mid-day for users
    // in UTC-negative zones — causing a second auto-open the same local day.
    const today = new Date().toLocaleDateString('en-CA');
    try {
      if (localStorage.getItem('whats-new-last-autoshown') === today) return; // already shown today
    } catch {
      // localStorage unavailable — fall through and evaluate once for this mount
    }
    // Auto-open only if the operator turned it on AND there's something to show.
    if (!this.whatsNewEnabled || this.whatsNewEntries.length === 0) return;
    try {
      localStorage.setItem('whats-new-last-autoshown', today);
    } catch {
      // localStorage unavailable — still open once for this mount
    }
    runInAction(() => { this.showWhatsNew = true; });
  }

  // eslint-disable-next-line no-empty-function,@typescript-eslint/no-empty-function
  init(): void {
  }

  @action.bound
  handleCreateClick(item) {
    const selectedId = item.detail.id;
    const [, documentType] = selectedId.split('-');

    switch (selectedId) {
      case 'folder':
        this.openCreateFolderModal();
        break;
      default:
        this.openCreateDocumentModal(documentType, !selectedId.startsWith('document-'));
    }
  }

  @action
  openCreateFolderModal() {
    this.folderModalStore = new AddFolderModalStore('Create folder', this.appStore);
    this.folderModalStore.init();
    this.folderModalStore.setVisible(true);
  }

  @action
  openCreateDocumentModal(documentType: string, templateIdentifier: boolean) {
    const documentTypeModel = this.appStore.getDocumentTypeModel(documentType);
    const header = templateIdentifier ? `Create ${documentTypeModel?.label} template` : `Create ${documentTypeModel?.label}`;
    this.documentModalStore = new CreateDocumentModalStore(header, this.appStore, documentType, templateIdentifier);
    this.documentModalStore.init();
    this.documentModalStore.setVisible(true);
  }

  @action
  async openPreferenceModal() {
    await this.appStore.api.query(getPreferences, {})
      .then(action(({ data }) => {
        const preferences = JSON.parse(data.getPreferences);
        this.organizationUrl = preferences?.organizationUrl;
        this.preferenceModalStore = new PreferenceModalStore(this.appStore, this.organizationUrl);
        this.preferenceModalStore.init();
        this.preferenceModalStore.setVisible(true);
      }));
  }

  @action
  isValidFolderAlias(input: string): boolean {
    const regex = /^[a-zA-Z_]+$/;
    return regex.test(input);
  }

  @action.bound
  async handleCreateFolder() {
    const model = this.folderModalStore.formToModel();
    let folderAliasIsValid = true;

    // Validate folderAlias
    if (model?.folderAlias !== undefined) {
      if (model.folderAlias.length >= 1) {
        if (!this.isValidFolderAlias(model.folderAlias)) {
          folderAliasIsValid = false;
        }
      }
    }

    const params = {
      folderName: model.folderName,
      parentId: model.parentId ? model.parentId.value : '',
      clonePermission: model.clonePermission ? model.clonePermission : false,
      isPublic: true,
      footerContent: model.footerContent,
      folderAlias: model.folderAlias,
    };

    if (!folderAliasIsValid) {
      return this.onFolderAliasFail();
    }

    return this.appStore.api.mutate(createFolder, params)
      // setTimeout is being used here because looks like when create returns the data might not be synced to ES
      // so we let it wait for 1 second and then call get metadata
      .then(response => setTimeout(() => this.onFolderCreateComplete(response), 1000))
      .catch(e => {
        this.onFolderCreateFail(e.errors[0]);
      });
  }

  @action
  onFolderCreateComplete(response) {
    const { data: { createFolder: { id } } } = response;
    this.appStore.navigateTo(NAVIGATION.FOLDER_MANAGEMENT_PAGE.LINK(id));
  }

  @action.bound
  onFolderAliasFail() {
    this.appStore?.appToastAlertStore.addToastAlert(
      {
        message: 'Pretty URL can only contain letters and underscores.',
      },
      'error'
    );
  }

  @action.bound
  onFolderAliasDuplicate() {
    this.appStore?.appToastAlertStore.addToastAlert(
      {
        message: 'Pretty URL has already been taken',
        statusCode: '400',
      },
      'error'
    );
  }

  @action
  onFolderCreateFail(error) {
    if (error.errorType === '401') {
      this.appStore?.appToastAlertStore.addToastAlert(
        {
          message: JSON.parse(error.message).message,
          statusCode: '401',
        },
        'error'
      );
    } else {
      this.appStore?.appToastAlertStore.addToastAlert(
        {
          message: 'Pretty URL has already been taken',
          statusCode: '400',
        },
        'error'
      );
    }
    // this.appStore.navigateTo(NAVIGATION.FOLDER_MANAGEMENT_PAGE.LINK(id));
  }

  @action
  onSearch(queryString) {
    this.appStore.navigateTo(NAVIGATION.SIMPLE_SEARCH_PAGE.LINK(queryString, this.appStore.rootFolderId), {}, true);
  }

  @action.bound
  handleCreateFolderCancelClick() {
    this.folderModalStore.setVisible(false);
  }

  @action.bound
  async handleCreateDocument() {
    const model = this.documentModalStore.formToModel();
    const params = {
      title: model.title,
      description: model.description ? model.description : '',
      documentType: model.documentType,
      isTemplate: model.isTemplate,
      parentFolderId: model.parentFolderId.value,
      parentTemplateId: model.parentTemplateId ? model.parentTemplateId.value : undefined,
      clonePermission: model.clonePermission,
      isPublic: model.isPublic ?? false,
      isShared: model.isShared ?? false,
      isRoot: model.isRoot ?? false,
    };
    return this.appStore!.api.mutate(createDocument, params)
      .then(value => this.onDocumentCreateComplete(value))
      .catch(e => this.onError(e.errors[0]));
  }

  @action.bound
  async handleSavePreference() {
    const model = this.preferenceModalStore.formToModel();
    const params = {
      organizationUrl: model.organizationUrl,
    };
    return this.appStore!.api.mutate(createPreferences, params)
      .then(value => this.onDocumentCreateComplete(value))
      .catch(e => this.onError(e.errors[0]));
  }

  onDocumentCreateComplete(value) {
    const { data: { createDocument: { id } } } = value;
    // Adding permissions to new documents might not be available immediately in auth service (due to replication),
    // therefor a delay of 3 second is introduced to mitigate access denied issue
    setTimeout(() => this.appStore.navigateTo(NAVIGATION.DOCUMENT_PAGE.LINK(id, 'draft')), 3000);
  }

  @action.bound
  handleCreateDocumentCancelClick() {
    this.documentModalStore.setVisible(false);
  }

  @action.bound
  handlePreferenceCancelClick() {
    this.preferenceModalStore.setVisible(false);
  }

  @action.bound
  handlePreferenceClickSuccess() {
    this.appStore.appToastAlertStore.addToastAlert({
      statusCode: '200',
      message: 'Preferenced Saved',
    }, 'success');
  }

  @action.bound
  onError(e: ErrorModel) {
    this.appStore.appToastAlertStore.addToastAlert(e, 'error');
  }

  /**
   * Handles clicks in the topnav menu.
   * @param {string} menuOption The ID from the menu item that was clicked.
   */
  @action
  handleMenuClick(menuOption: string): void {

    switch (menuOption) {
      case 'prefrence':
        this.openPreferenceModal();
        this.showPreference = true;
        break;
      default: null;
    }
  }
}

export const CACHE_PREFIX = 'TopNav-';
